import { IconButton, Stack, Tooltip, Typography } from '@mui/material';
import { ConditionOperatorEnum, type Condition } from '@repo/api-gw-sdk';
import type { Column } from '@tanstack/react-table';

import { useViewParameters } from '@/contexts/navigation';
import {
  collectValueConditions,
  createCombineCondition,
  removeValueCondition,
  type FilterProperty,
} from '@/types/advanceFilter';

import { getProperties } from '../queryBuilder/propertiesUtils';

export const FiltersBar = ({
  columns,
}: {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  columns: Column<any>[];
}) => {
  const { query, setParams } = useViewParameters();

  return (
    <UnmanagedFiltersBar
      columns={columns}
      filter={query}
      onFilterChange={(newFilter) =>
        setParams((state) => ({
          ...state,
          query: newFilter,
        }))
      }
    />
  );
};

const UnmanagedFiltersBar = ({
  columns,
  filter,
  onFilterChange,
}: {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  columns: Column<any>[];
  filter: Condition | undefined;
  onFilterChange: (filter: Condition) => void;
}) => {
  if (!filter) {
    return null;
  }

  const properties = columns.flatMap((x) => getProperties(x));
  const allConditions = collectValueConditions(filter).reduce<
    { condition: Condition; value: string; property: FilterProperty }[]
  >((acc, condition) => {
    condition?.value?.forEach((value) => {
      const property = properties.find((x) => x.name === condition.property);

      if (property) {
        acc.push({ condition, value, property });
      }
    });
    return acc;
  }, []);

  const visibleConditions = allConditions.filter(
    ({ condition, property }) => !property.filter?.shouldHideBadge?.(condition)
  );

  if (!visibleConditions.length) {
    return null;
  }

  return (
    <Stack
      width='70%'
      padding='0 12px 24px 40px'
      direction={'row'}
      flexWrap={'wrap'}
      gap={'8px'}
      data-testid='filters-bar'
    >
      {visibleConditions.map(({ condition, value, property }) => {
        const Badge = property.filter?.Badge;

        return (
          <Tooltip
            key={`${condition.property} ${value}`}
            title={property?.displayName || ''}
          >
            <Stack
              direction={'row'}
              className='py-[4px] px-[12px] inline-flex rounded-[4px]'
              sx={{ border: '1px solid var(--mui-palette-primary-main)' }}
              alignItems={'center'}
            >
              <Typography
                component={'span'}
                className='font-semibold cursor-default text-ellipsis overflow-hidden	whitespace-nowrap	max-w-[200px]'
              >
                {Badge && <Badge conditionValue={value} />}
                {!Badge && value}
              </Typography>
              <i
                className='material-symbols-close-rounded text-[22px] ml-[4px] cursor-pointer'
                onClick={() =>
                  onFilterChange(removeValueCondition(filter, condition, value))
                }
              />
            </Stack>
          </Tooltip>
        );
      })}
      <Tooltip title='Clear all'>
        <IconButton
          onClick={() =>
            onFilterChange(
              createCombineCondition(
                allConditions
                  .map((x) => x.condition)
                  .filter((x) => x.operator === ConditionOperatorEnum.NotIn)
              )
            )
          }
        >
          <i className='material-symbols-filter-alt-off-outline-rounded text-[var(--mui-palette-text-primary)] h-[20px] w-[20px]'></i>
        </IconButton>
      </Tooltip>
    </Stack>
  );
};
